import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { inject, observer } from 'mobx-react';
import { appInsights, logException } from '../../services/appInsights';
import { callApi } from '../../util/apiWrapper';
import { logAudit } from '../../util/auditUtil';
import useAuthRedirect from '../../hooks/authRedirectHook';
import TailwindScope from '../Shared/TailwindScope';
import cn from 'classnames';
import InputField from '../Shared/Tailwind/InputField';
import PhoneInput from './PhoneInput';
import { formatPhoneNumber } from '../../util/miscUtil';

const styles = {
  fullWidth: 'w-full',
  wrapper: 'flex max-w-[610px] items-start justify-start',
  container: 'max-w-[610px] ml-5',
  instructionsContainer: 'w-full flex flex-col items-start justify-start my-2 sm:mt-0',
  instructions: 'mt-3 sm:mt-[-3px] text-base sm:text-lg text-left',
  contactInfo: 'p-2 text-md text-brand-navy',
  contentSpacing: 'w-full pb-2 pt-2 space-y-4',
  contentForm: 'w-full px-4 sm:p-6 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-400 pb-5',
  firstLabel: 'w-full text-left',
  otherLabel: 'w-full text-left mt-5',
  phoneButtonContainer: 'w-full flex flex-col sm:flex-row sm:items-end gap-2 pt-4 mb-7',
  phoneFields: 'flex flex-col sm:flex-row gap-2 items-stretch sm:items-end flex-1 w-full',
  phoneField: 'w-full sm:flex-1 min-w-0 self-stretch flex flex-col justify-end',
  updatePhoneButton:
    'w-full sm:w-40 h-11 bg-blue rounded-xl border-none text-white text-md font-bold font-sans uppercase tracking-wide cursor-pointer hover:shadow-lg transition-shadow',
  buttonContainer: 'w-full flex justify-between items-center gap-2 pt-4 mb-7',
  cancelButton:
    'w-full sm:w-44 h-12 bg-brand-gold rounded-3xl border-none text-white text-md font-bold font-sabs uppercase tracking-wide cursor-pointer hover:shadow-lg transition-shadow',
  sendButton:
    'w-full sm:w-44 h-12 bg-blue rounded-3xl border-none text-white text-md font-bold font-sans uppercase tracking-wide cursor-pointer hover:shadow-lg transition-shadow',
  toastFrame:
    'fixed top-4 right-4 z-[9999] flex items-center gap-3 min-w-[300px] max-w-[650px] px-4 py-3 bg-orange text-white rounded-lg shadow-lg animate-fadeIn',
  toastMessage: 'flex-1 text-md font-medium',
  toastCloseButton: 'flex-shrink-0 p-1 hover:bg-white/20 rounded transition-colors',
  fieldGroup: 'self-stretch flex flex-col items-start',
  fieldLabel: 'text-left text-blue-950 text-sm font-semibold leading-5',
  textInput: 'w-full h-11 px-3 py-5 rounded-sm outline outline-1 outline-offset-[-1px] outline-zinc-300 flex items-start',
  selectInput: 'w-full h-11 px-3 py-2.5 rounded-sm outline outline-1 outline-offset-[-1px] outline-zinc-300',
  textArea: 'w-full min-h-28 px-3 py-2.5 rounded-sm outline outline-1 outline-offset-[-1px] outline-zinc-300',
  sectionTop: 'self-stretch pt-6 flex flex-col justify-start items-start text-left',
  label: 'justify-start text-brand-navy text-sm font-semibold leading-5',
  requiredMark: 'text-red-600 text-xl',
  radioRowWrap: 'self-stretch flex flex-col justify-start items-start',
  radioRow: 'self-stretch inline-flex flex-wrap justify-start items-start gap-4',
  buttonToTextSpace: 'self-stretch inline-flex items-center gap-2',
  spaceBetweenItems: 'text-gray-900 text-base font-normal leading-12',
};

const validPhoneTypes = [
  { value: 'Mobile', label: 'Mobile' },
  { value: 'Home', label: 'Home' },
  { value: 'Work', label: 'Work' },
];
const validPhoneTypeValues = validPhoneTypes.map((t) => t.value);

const AskQuestion = inject(
  'SeniorStore',
  'AuthStore'
)(
  observer((props) => {
    const store = props.SeniorStore;
    const auth = props.AuthStore;

    const { handleSaveReturn } = props;
    const { setShowConfirm } = props;
    const [showWarning, setShowWarning] = useState(false);
    const [warning, setWarning] = useState('');
    const [submittedAttempted, setSubmittedAttempted] = useState(false);
    const [phoneType, setPhoneType] = useState<string>('Mobile');

    useEffect(() => {
      const alumniPhone = store?.senior?.alumni_phone_1 ? String(store.senior.alumni_phone_1).replace(/\D/g, '').slice(0, 10) : '';
      const seniorPhoneType = store?.senior?.alumni_phone_1_type;

      setForm((prev) => (prev.phone === alumniPhone ? prev : { ...prev, phone: alumniPhone }));
      if (seniorPhoneType && validPhoneTypeValues.includes(seniorPhoneType)) {
        setPhoneType(seniorPhoneType);
      }
    }, [store?.senior?.alumni_phone_1, store?.senior?.alumni_phone_1_type, validPhoneTypeValues]);

    async function updateAlumniPhone1() {
      try {
        const senior = store?.senior
          ? {
              ...store.senior,
              alumni_phone_1: form.phone,
              alumni_phone_1_type: phoneType,
              contact_updated_at: new Date(),
              contact_updated_by: auth.user.result.id,
            }
          : { alumni_phone_1: form.phone, alumni_phone_1_type: phoneType, contact_updated_at: new Date(), contact_updated_by: auth.user.result.id };
        store.setSenior(senior);

        store.setLoading(true);
        await callApi(auth, 'student', 'PUT', {
          item: senior,
        });

        store.setShowBasicInfo(false);
        store.setLoading(false);
      } catch (error) {
        console.error('Failed to update alumni phone:', error);
        store.setShowBasicInfo(false);
        store.setLoading(false);
      }
    }

    const contactMethodOptions = [
      { label: 'Email', value: 'Email' },
      { label: 'Phone', value: 'Phone' },
    ];

    const handleClose = () => {
      setShowWarning(false);
    };

    // Auto-hide warning toast after 6 seconds
    useEffect(() => {
      if (showWarning) {
        const timer = setTimeout(() => {
          setShowWarning(false);
        }, 6000);
        return () => clearTimeout(timer);
      }
    }, [showWarning]);

    // prettier-ignore
    const [form, setForm] = useState({
      closeQuestion: false,
      contactMethod: '',
      phone: '',
      subject: '',
      question: '',
      status: '',
    });

    const [phone1IsValid, setPhone1IsValid] = useState(true);

    // Redirect user to login page if token is no longer available.
    useAuthRedirect(auth);

    useEffect(() => {
      const staffList = async () => {
        try {
          if (store.staffList && store.staffList.length < 1) {
            store.setLoading(true);
            store.setSearching(true);
            const staff = await callApi(auth, 'staff', 'GET');
            store.setStaffList(staff.result);
            store.setSearching(false);
            store.setLoading(false);
          }
        } catch (error) {
          store.setSearching(false);
          store.setLoading(false);
          console.warn('Error fetching staff list:', error);
          logException(error);
        }
      };
      staffList();
    }, [store.awardPaymentQuestion, auth, store]);

    const handleSaveAwardPaymentQuestion = () => {
      writeAwardPaymentQuestionRequest();
    };

    async function writeAwardPaymentQuestionRequest() {
      if (canSubmit()) {
        try {
          store.setLoading(false);
          store.setSearching(true);

          const item = {
            created_by: auth.user.result.id,
            created_at: new Date(),
            updated_by: auth.user.result.id,
            updated_at: new Date(),
            status: 'submitted',
            subject: form.subject,
            contact_method: form.contactMethod,
            question_text: form.question,
          };

          await callApi(auth, 'payment/question', 'POST', { item });

          await sendAwardPaymentQuestionSubmittedEmail(auth.user.result.id, auth.user.result.email, form.subject, form.question, form.contactMethod);

          appInsights.trackTrace({ message: `Payment Question submitted by ${auth.user.result.email}` });
          const auditObj = {
            audit_ts: new Date(),
            user_id: auth.user.result.id,
            username: auth.user.result.first_name + ' ' + auth.user.result.last_name,
            audit_type: 'payment question',
            action: 'submitted',
            value: JSON.stringify(form).substring(0, 999),
          };
          logAudit(auth, auditObj);

          store.setSearching(false);
          store.setLoading(false);

          setForm({
            closeQuestion: false,
            contactMethod: '',
            phone: store?.senior?.alumni_phone_1 ? String(store.senior.alumni_phone_1).replace(/\D/g, '').slice(0, 10) : '',
            subject: '',
            question: '',
            status: '',
          });
          setSubmittedAttempted(false);

          setShowConfirm(true);
          handleSaveReturn();
        } catch (error) {
          logException(error);
          store.setSearching(false);
          store.setLoading(false);
          console.warn('Error writing updated award payment question request:', error);
        }
      }
    }

    const canSubmit = () => {
      let rc = true;
      setSubmittedAttempted(true);

      if (!form.subject || form.subject.trim().length === 0) rc = false;
      if (!form.contactMethod || form.contactMethod.length === 0) rc = false;
      if (!form.question || form.question.trim().length === 0) rc = false;
      if (form.contactMethod === 'Phone' && (!form.phone || form.phone.length !== 10 || !phone1IsValid)) rc = false;
      if (form.contactMethod === 'Phone' && (!phoneType || phoneType.length === 0)) rc = false;

      if (!rc) {
        setWarning('The award payment question could not be submitted. See the errors in red.');
        setShowWarning(true);
      }
      return rc;
    };

    const isFormDisabled = () => {
      if (form.status === 'submitted' || form.status === 'closed') {
        return true;
      }
      return false;
    };

    const isButtonDisabled = () => {
      if (showWarning) {
        return true;
      }
      return false;
    };

    const sendAwardPaymentQuestionSubmittedEmail = async (student_id, email, subject, question, contactMethod) => {
      try {
        store.setLoading(true);
        store.setSearching(true);
        await callApi(auth, 'payment/question/submitted/email', 'POST', {
          StudentId: student_id,
          Email: email,
          Subject: subject,
          Question: question,
          ContactMethod: contactMethod,
        });
        store.setSearching(false);
        store.setLoading(false);
      } catch (error) {
        logException(error);
        store.setSearching(false);
        store.setLoading(false);
        console.warn('Error sending payment question submitted email:', error);
      }
    };

    const renderToast = () => {
      if (!showWarning) return null;

      return ReactDOM.createPortal(
        <div className='tw-enabled font-sans'>
          {showWarning && (
            <div className={cn(styles.toastFrame)}>
              <span className={cn(styles.toastMessage)}>{warning}</span>
              <button onClick={handleClose} className={cn(styles.toastCloseButton)} aria-label='close'>
                <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 21 21'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>
          )}
        </div>,
        document.body
      );
    };

    return (
      <>
        <TailwindScope className={cn(styles.fullWidth)} data-testid='award-payment-requests-summary-page'>
          <div className={cn(styles.wrapper)}>
            <div className={cn(styles.container)}>
              <div className={cn(styles.contentSpacing)}>
                <div className={cn(styles.contentForm)}>
                  <div className={cn(styles.instructionsContainer)}>
                    <h3 className={cn(styles.instructions)}>
                      Have a question about your award payment request? Send us a message and we'll get right back to you as soon as possible.
                    </h3>
                  </div>
                  <div className={cn(styles.firstLabel)}>
                    <InputField
                      name='subject'
                      label='Subject:'
                      type='text'
                      value={form.subject}
                      onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                      placeholder='Brief summary of your question'
                      wrapperClassName={styles.fieldGroup}
                      labelClassName={styles.fieldLabel}
                      inputClassName={cn(styles.textInput, 'placeholder:text-neutral-950/50')}
                      required={true}
                      showRequiredErrors={submittedAttempted}
                    />
                  </div>
                  <div className={cn(styles.otherLabel)}>
                    <InputField
                      name='question-text'
                      label='Your Question'
                      type='textarea'
                      value={form.question}
                      onChange={(event) => setForm((prev) => ({ ...prev, question: event.target.value }))}
                      placeholder='Type your question here.'
                      wrapperClassName={styles.fieldGroup}
                      labelClassName={styles.fieldLabel}
                      inputClassName={styles.textArea}
                      required={true}
                      showRequiredErrors={submittedAttempted}
                    />
                  </div>
                  <div className={cn(styles.otherLabel)}>
                    <InputField
                      name='contactMethod'
                      label='Preferred Method of Contact'
                      type='radio'
                      options={contactMethodOptions}
                      value={form.contactMethod}
                      onChange={(value) => setForm((prev) => ({ ...prev, contactMethod: value }))}
                      required
                      showRequiredErrors={submittedAttempted}
                      wrapperClassName={styles.sectionTop}
                      labelClassName={styles.label}
                      requiredMarkClassName={styles.requiredMark}
                      groupWrapperClassName={styles.radioRowWrap}
                      groupClassName={styles.radioRow}
                      radioItemClassName={styles.buttonToTextSpace}
                      radioLabelClassName={styles.spaceBetweenItems}
                    />
                  </div>
                  {form.contactMethod === 'Email' && !showWarning && (
                    <h5 className={cn(styles.contactInfo)}>
                      The Successful Futures team will contact you at {store?.senior?.user?.email || auth?.user?.result?.email || ''}.
                    </h5>
                  )}
                  {form.contactMethod === 'Phone' && (
                    <>
                      <div className={cn(styles.phoneButtonContainer)}>
                        <div className={cn(styles.phoneFields, 'flex-1')}>
                          <PhoneInput
                            name='phone'
                            label='Phone Number:'
                            value={form.phone}
                            onChange={(val, isValid) => {
                              setForm((prev) => ({ ...prev, phone: val }));
                              setPhone1IsValid(isValid);
                            }}
                            required={form.contactMethod === 'Phone'}
                            disabled={isFormDisabled()}
                            showRequiredErrors={submittedAttempted}
                            wrapperClassName={cn(styles.fieldGroup, styles.phoneField)}
                            labelClassName={styles.fieldLabel}
                            inputClassName={cn(styles.textInput, 'placeholder:text-neutral-950/50')}
                            dataTestId='phone-number-field'
                          />
                          <InputField
                            name='phone_type'
                            label='Phone Type:'
                            type='select'
                            value={phoneType}
                            onChange={(value) => setPhoneType(value)}
                            options={validPhoneTypes}
                            required={form.contactMethod === 'Phone'}
                            showRequiredErrors={submittedAttempted}
                            wrapperClassName={cn(styles.fieldGroup, styles.phoneField, 'relative')}
                            labelClassName={styles.fieldLabel}
                            inputClassName={styles.selectInput}
                            wrapperTestId='phone-type-field'
                          />
                        </div>
                        <button
                          onClick={(event) => updateAlumniPhone1()}
                          disabled={!phone1IsValid || form.phone.length !== 10 || isButtonDisabled()}
                          className={cn(styles.updatePhoneButton)}
                        >
                          {'UPDATE PHONE'}
                        </button>
                      </div>
                      {!showWarning && (
                        <h5 className={cn(styles.contactInfo)}>
                          The Successful Futures team will contact you at{' '}
                          {formatPhoneNumber(store?.senior?.alumni_phone_1) || formatPhoneNumber(auth?.user?.result?.phone) || ''}.
                        </h5>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className={cn(styles.buttonContainer)}>
                <button onClick={(event) => handleSaveReturn()} className={cn(styles.cancelButton)}>
                  {'CANCEL'}
                </button>
                <button onClick={(event) => handleSaveAwardPaymentQuestion()} disabled={isButtonDisabled()} className={cn(styles.sendButton)}>
                  {'SEND QUESTION'}
                </button>
              </div>
            </div>
          </div>
        </TailwindScope>
        {renderToast()}
      </>
    );
  })
);

export default AskQuestion;
